import BeeswaxCatchupBehavior from '@/Enums/Beeswax/BeeswaxCatchupBehavior.enum';
import BeeswaxPacing from '@/Enums/Beeswax/BeeswaxPacing.enum';
import BeeswaxPacingBehavior from '@/Enums/Beeswax/BeeswaxPacingBehavior.enum';
import NumberInput from '@/components/NumberInput';
import InfoTooltip from '@/components/Tooltip/InfoTooltip';
import { BeeswaxPacingLabel } from '@/constants/lineItems/beeswaxPacingLabels';
import { Autocomplete, FormLabel, TextField, capitalize } from '@mui/material';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

const BASE_CLASSNAME = 'col-span-1';

interface IProps {
  index: number;
}

const PacingSection = ({ index }: IProps) => {
  const { control, setValue } = useFormContext();

  const pacing = useWatch({ control, name: 'pacing' });

  useEffect(() => {
    if (pacing === BeeswaxPacing.ASAP) {
      setValue('pacingBehavior', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('catchupBehavior', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [pacing]);

  return (
    <div className='mt-3'>
      <div className='grid grid-cols-4 gap-x-4'>
        <div className={BASE_CLASSNAME}>
          <FormLabel htmlFor={`pacing-${index}`}>Pacing</FormLabel>
          <Controller
            name='pacing'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                id={`pacing-${index}`}
                options={Object.values(BeeswaxPacing)}
                fullWidth
                disableClearable
                onChange={(_, data) => field.onChange(data)}
                getOptionLabel={(option: BeeswaxPacing) => capitalize(BeeswaxPacingLabel[option] || '')}
                isOptionEqualToValue={(option, selectValue) => selectValue && option === selectValue}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder='Select Pacing'
                    variant='outlined'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          />
          {pacing === BeeswaxPacing.ASAP && (
            <span className='text-[0.75rem] '>
              ASAP Pacing could lead to delivering all impressions in the next few hours.
            </span>
          )}
        </div>
        {pacing !== BeeswaxPacing.ASAP && (
          <>
            <div className={BASE_CLASSNAME}>
              <FormLabel htmlFor={`pacing-behavior-${index}`}>Pacing Behavior</FormLabel>
              <Controller
                name='pacingBehavior'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id={`pacing-behavior-${index}`}
                    options={Object.values(BeeswaxPacingBehavior)}
                    fullWidth
                    disableClearable
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={option => capitalize(option || '')}
                    isOptionEqualToValue={(option, selectValue) => selectValue && option === selectValue}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder='Select Pacing Behavior'
                        variant='outlined'
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </div>
            <div className={classNames(BASE_CLASSNAME, 'relative')}>
              <FormLabel className='form-label-with-tooltip' htmlFor={`catchup-behavior-${index}`}>
                Catchup Behavior
                <InfoTooltip
                  title={
                    <ul className='space-y-2 list-disc list-inside'>
                      <li>
                        Immediate - When inventory becomes available, it will try to use all spend immediately to
                        catchup.
                      </li>
                      <li>Even - Will evenly catchup to the desired spend rate.</li>
                      <li>
                        Smooth - Will spend slightly more in the beginning when the inventory is available and gradually
                        catchup with the target spend rate.
                      </li>
                    </ul>
                  }
                  className='form-label-tooltip'
                />
              </FormLabel>
              <Controller
                name='catchupBehavior'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id={`catchup-behavior-${index}`}
                    options={Object.values(BeeswaxCatchupBehavior)}
                    fullWidth
                    disableClearable
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={option => capitalize(option || '')}
                    isOptionEqualToValue={(option, selectValue) => selectValue && option === selectValue}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder='Select Catchup Behavior'
                        variant='outlined'
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </div>
          </>
        )}
        <div className={BASE_CLASSNAME}>
          <FormLabel htmlFor={`daily-impressions-cap-${index}`}>Daily Impression Cap</FormLabel>
          <Controller
            name='dailyBudget'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                {...field}
                // @ts-expect-error jsx component prop
                id={`daily-impressions-cap-${index}`}
                fullWidth
                disableInputArrows
                NumberInputProps={{ thousandsSeparator: ',', unmask: true }}
                placeholder='0'
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default PacingSection;
